import { useEffect, useState } from "react";
import { api, mediaUrl } from "@/lib/api";
import { PageTitle, Card, GBtn, Field, inputCls } from "./ui";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { Save, Plus, Trash2 } from "lucide-react";

export default function SettingsAdmin() {
  const [s, setS] = useState(null);
  const [faqs, setFaqs] = useState([]);

  useEffect(() => {
    api.get("/admin/settings").then((r) => setS(r.data));
    api.get("/admin/faqs").then((r) => setFaqs(r.data));
  }, []);

  const set = (k, v) => setS((x) => ({ ...x, [k]: v }));
  const save = async () => {
    await api.put("/admin/settings", s);
    await api.put("/admin/faqs", { items: faqs });
    toast.success("Settings saved");
  };
  const uploadPhoto = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try { const { data } = await api.post("/admin/upload-image", fd); set("about_photo", data.url); toast.success("Photo uploaded"); }
    catch { toast.error("Upload failed"); }
  };
  const setAvail = (day, field, val) => setS((x) => ({
    ...x, availability: { ...(x.availability || {}), [day]: { ...((x.availability || {})[day] || {}), [field]: val } },
  }));

  if (!s) return <div className="text-white/40">Loading…</div>;

  const T = ({ k, label, type = "text" }) => (
    <Field label={label}><input type={type} value={s[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`setting-${k}`} className={inputCls} /></Field>
  );

  return (
    <>
      <PageTitle title="Site Settings" subtitle="Edit everything shown across the site" right={<GBtn onClick={save} data-testid="settings-save"><Save size={16} /> Save all</GBtn>} />

      <div className="space-y-4">
        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Contact & Socials</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="whatsapp_number" label="WhatsApp number (display)" />
            <T k="whatsapp_link" label="WhatsApp click-to-chat link" />
            <T k="contact_email" label="Public contact email" />
            <T k="notification_email" label="Notifications go to (your inbox)" />
            <T k="youtube_url" label="YouTube URL" />
            <T k="tiktok_url" label="TikTok URL" />
            <T k="instagram_url" label="Instagram URL" />
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Announcement Bar</h3>
          <div className="flex items-center gap-3 mb-3"><Switch checked={!!s.announcement_enabled} onCheckedChange={(v) => set("announcement_enabled", v)} data-testid="setting-announcement_enabled" /> <span className="text-sm">Show announcement bar</span></div>
          <T k="announcement_bar" label="Announcement text" />
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Homepage Videos</h3>
          <label className="flex items-center gap-2 text-sm mb-2"><Switch checked={s.youtube_auto_fetch !== false} onCheckedChange={(v) => set("youtube_auto_fetch", v)} data-testid="setting-youtube_auto_fetch" /> Auto-fetch latest videos from my YouTube channel</label>
          <p className="text-[11px] text-white/40">When on, the homepage always shows your newest uploads (from the YouTube URL above). When off, it uses the manual list in the Videos section.</p>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Homepage Hero</h3>
          <Field label="Hero title"><input value={s.hero_title ?? ""} onChange={(e) => set("hero_title", e.target.value)} className={inputCls} /></Field>
          <div className="mt-3"><Field label="Hero subtitle"><textarea rows={3} value={s.hero_subtitle ?? ""} onChange={(e) => set("hero_subtitle", e.target.value)} className={inputCls} /></Field></div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">About Page</h3>
          <Field label="About text (blank lines separate paragraphs)"><textarea rows={7} value={s.about_text ?? ""} onChange={(e) => set("about_text", e.target.value)} className={inputCls} /></Field>
          <div className="mt-3">
            <Field label="Your photo">
              {s.about_photo && <img src={mediaUrl(s.about_photo)} alt="" className="h-28 rounded-lg object-cover mb-2 border border-white/10" />}
              <input type="file" accept="image/*" onChange={(e) => e.target.files[0] && uploadPhoto(e.target.files[0])} className="text-xs text-white/50" />
            </Field>
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Community & Booking</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="telegram_link" label="Telegram join link" />
            <div className="flex items-end pb-2"><label className="flex items-center gap-2 text-sm"><Switch checked={!!s.telegram_link_enabled} onCheckedChange={(v) => set("telegram_link_enabled", v)} data-testid="setting-telegram_enabled" /> Show direct Telegram link</label></div>
            <T k="calendly_link" label="Calendly / booking embed link" />
            <T k="delivery_time" label="Delivery time text" />
          </div>
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            <T k="whatsapp_community_price" label="Paid WhatsApp group price (£/month)" type="number" />
          </div>
          <div className="mt-3"><Field label="Paid WhatsApp community details"><textarea rows={2} value={s.whatsapp_community_details ?? ""} onChange={(e) => set("whatsapp_community_details", e.target.value)} className={inputCls} /></Field></div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Service Prices (£)</h3>
          <div className="grid sm:grid-cols-3 gap-3">
            <T k="growth_coaching_price" label="Growth coaching / month" type="number" />
            <T k="youtube_setup_price" label="YouTube Launch Setup" type="number" />
            <T k="consultation_price" label="Consultation (0 = enquiry)" type="number" />
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-2">Consultation Availability</h3>
          <p className="text-[11px] text-white/40 mb-4">Set your weekly hours (UK time). The booking calendar auto-generates 30-minute slots from 48 hours ahead up to 2 weeks out, and hides any slot that's already been paid for.</p>
          <div className="space-y-2">
            {[["mon", "Monday"], ["tue", "Tuesday"], ["wed", "Wednesday"], ["thu", "Thursday"], ["fri", "Friday"], ["sat", "Saturday"], ["sun", "Sunday"]].map(([d, label]) => {
              const cfg = (s.availability || {})[d] || {};
              return (
                <div key={d} className="flex flex-wrap items-center gap-3 rounded-lg border border-white/10 bg-black/20 px-3 py-2">
                  <label className="flex items-center gap-2 text-sm w-32">
                    <Switch checked={!!cfg.enabled} onCheckedChange={(v) => setAvail(d, "enabled", v)} data-testid={`avail-${d}-enabled`} />
                    {label}
                  </label>
                  <input type="time" value={cfg.start || "18:00"} disabled={!cfg.enabled} onChange={(e) => setAvail(d, "start", e.target.value)} data-testid={`avail-${d}-start`} className={`${inputCls} w-32 disabled:opacity-40`} />
                  <span className="text-white/40 text-sm">to</span>
                  <input type="time" value={cfg.end || "21:00"} disabled={!cfg.enabled} onChange={(e) => setAvail(d, "end", e.target.value)} data-testid={`avail-${d}-end`} className={`${inputCls} w-32 disabled:opacity-40`} />
                </div>
              );
            })}
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">PayPal (optional — alongside Stripe)</h3>
          <p className="text-[11px] text-white/40 mb-3">Add your PayPal API credentials from developer.paypal.com → Apps &amp; Credentials. A PayPal button appears at checkout only when both fields are filled. Your secret is never shown on the public site.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="paypal_client_id" label="PayPal Client ID" />
            <T k="paypal_secret" label="PayPal Secret" />
            <Field label="Mode">
              <select value={s.paypal_mode || "sandbox"} onChange={(e) => set("paypal_mode", e.target.value)} data-testid="setting-paypal_mode" className={inputCls}>
                <option value="sandbox">Sandbox (test)</option>
                <option value="live">Live (real payments)</option>
              </select>
            </Field>
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Spam Protection (Google reCAPTCHA v3)</h3>
          <p className="text-[11px] text-white/40 mb-3">Get keys at google.com/recaptcha/admin (choose "Score based v3"), add your domain, then paste them here. When both keys are set, all public forms are protected automatically. Leave blank to use the built-in honeypot only.</p>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="recaptcha_site_key" label="Site key (public)" />
            <T k="recaptcha_secret_key" label="Secret key (private)" />
            <T k="recaptcha_threshold" label="Score threshold (0-1, default 0.5)" type="number" />
          </div>
        </Card>

        <Card>
          <h3 className="font-display text-lg text-[#D4AF37] mb-4">Tracking</h3>
          <div className="grid sm:grid-cols-2 gap-3">
            <T k="google_analytics_id" label="Google Analytics ID" />
            <T k="meta_pixel_id" label="Meta Pixel ID" />
          </div>
        </Card>

        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg text-[#D4AF37]">Homepage FAQs</h3>
            <GBtn variant="ghost" onClick={() => setFaqs([...faqs, { q: "", a: "" }])}><Plus size={15} /> Add FAQ</GBtn>
          </div>
          <div className="space-y-3">
            {faqs.map((f, i) => (
              <div key={i} className="rounded-lg border border-white/10 p-3 space-y-2">
                <div className="flex gap-2">
                  <input value={f.q} onChange={(e) => setFaqs(faqs.map((x, idx) => idx === i ? { ...x, q: e.target.value } : x))} placeholder="Question" className={inputCls} />
                  <button onClick={() => setFaqs(faqs.filter((_, idx) => idx !== i))} className="text-red-400 px-2"><Trash2 size={16} /></button>
                </div>
                <textarea rows={2} value={f.a} onChange={(e) => setFaqs(faqs.map((x, idx) => idx === i ? { ...x, a: e.target.value } : x))} placeholder="Answer" className={inputCls} />
              </div>
            ))}
          </div>
        </Card>

        <GBtn onClick={save} className="w-full"><Save size={16} /> Save all settings</GBtn>
      </div>
    </>
  );
}
